/* ============================================
   Globdrop — App Entry Point
   ============================================ */

import './styles/tokens.css';
import './styles/reset.css';
import './styles/layout.css';
import './styles/animations.css';
import './styles/game.css';
import './styles/flags.css';

import { GameEngine } from './engine/game-engine.ts';
import { FlagGameEngine } from './engine/flag-engine.ts';
import { CountdownTimer } from './engine/timer.ts';
import type { Scenario, ScenarioIndex } from './types/scenario.ts';
import type { FlagDifficulty } from './types/flag.ts';
import {
  renderScenarioSelection,
  renderModeSelection,
  renderCountdown,
  renderPlaying,
  renderFeedback,
  renderResults,
  showFeedbackOnMap,
} from './components/ui-renderer.ts';
import {
  renderFlagDifficultySelection,
  renderFlagGameModeSelection,
  renderFlagCountdown,
  renderFlagPlaying,
  renderFlagFeedback,
  renderFlagResults,
} from './components/flag-ui-renderer.ts';

import { t } from './engine/i18n.ts';
import { track, initAnalytics } from './utils/analytics.ts';

// ── App State ──
const engine = new GameEngine();
const flagEngine = new FlagGameEngine();
let currentScenario: Scenario | null = null;
let gameTimer: CountdownTimer | null = null;
let timerUpdateFn: ((remaining: number) => void) | null = null;

/** game_start: modo clasico (mapa) o banderas, y el id del escenario. */
function trackGameStart(mode: 'classic' | 'flags', map: string): void {
  track('game_start', { mode, map });
}

function initThemeToggle(): void {
  const toggleBtn = document.getElementById('theme-toggle');
  if (!toggleBtn) return;

  toggleBtn.addEventListener('click', () => {
    let currentTheme = document.documentElement.getAttribute('data-theme');
    if (!currentTheme) {
      const isSystemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      currentTheme = isSystemDark ? 'dark' : 'light';
    }

    const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
    document.documentElement.setAttribute('data-theme', newTheme);
    localStorage.setItem('theme', newTheme);

    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', newTheme === 'dark' ? '#0f0f1a' : '#f8fafc');
    }
  });

  // Listen to OS theme change and update browser UI headers if user hasn't overridden it
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
    if (!localStorage.getItem('theme')) {
      const metaThemeColor = document.querySelector('meta[name="theme-color"]');
      if (metaThemeColor) {
        metaThemeColor.setAttribute('content', e.matches ? '#0f0f1a' : '#f8fafc');
      }
    }
  });
}

// ── Boot ──
async function boot(): Promise<void> {
  initThemeToggle();
  initAnalytics();
  const footerAbout = document.getElementById('footer-about');
  if (footerAbout) footerAbout.textContent = t('footer.about');
  try {
    const res = await fetch('/data/scenarios/index.json');
    const index: ScenarioIndex = await res.json();
    
    showScenarioScreen(index);
  } catch (err) {
    console.error('Failed to load scenario index:', err);
    document.querySelector('#main')!.innerHTML = `
      <div class="screen active">
        <h1>Error</h1>
        <p class="text-muted">${t('app.error.loading')}</p>
      </div>
    `;
  }
}

/** 
 * Randomizes the order of an array in-place using Fisher-Yates.
 * Returns the shuffled array.
 */
function shuffle<T>(array: T[]): T[] {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const temp = array[i]!;
    array[i] = array[j]!;
    array[j] = temp;
  }
  return array;
}

// ── Screen Controllers ──

function showScenarioScreen(index: ScenarioIndex): void {
  engine.goHome();
  
  // Randomize scenarios order at each entry
  shuffle(index.scenarios);

  renderScenarioSelection(index, async (scenarioId: string) => {
    try {
      const meta = index.scenarios.find(s => s.id === scenarioId);
      if (meta && (meta as any).isFlags) {
        showFlagModeScreen(scenarioId, meta, index);
        return;
      }

      const res = await fetch(`/data/scenarios/${scenarioId}.json`);
      currentScenario = await res.json();
      
      // Inject icon from index
      if (meta?.icon && currentScenario) currentScenario.icon = meta.icon;

      engine.selectScenario(currentScenario!);
      showModeScreen(index);
    } catch (err) {
      console.error('Failed to load scenario:', err);
    }
  });
}

function showModeScreen(index: ScenarioIndex): void {
  if (!currentScenario) return;
  renderModeSelection(
    currentScenario,
    (mode) => {
      engine.startGame(mode);
      trackGameStart('classic', currentScenario!.id);
      showCountdownScreen(index);
    },
    () => showScenarioScreen(index)
  );
}


function showCountdownScreen(index: ScenarioIndex): void {
  const session = engine.getSession();
  renderCountdown(session, () => {
    engine.countdownDone();
    showPlayingScreen(index);
  });
}

function showPlayingScreen(index: ScenarioIndex): void {
  const session = engine.getSession();
  const mapSrc = `/${session.scenario!.map}`;

  renderPlaying(
    session,
    mapSrc,
    // On map click
    (pixel, imageSize) => {
      gameTimer?.stop();
      const result = engine.handleMapClick(pixel, imageSize);

      // Show pinpoints on map
      showFeedbackOnMap(
        pixel,
        { x: result.place.x, y: result.place.y },
        session.scenario!.boundingBox,
        session.scenario!.coordinateSystem
      );

      // Show feedback overlay
      const updatedSession = engine.getSession();
      renderFeedback(result, updatedSession, () => {
        engine.nextRound();
        const s = engine.getSession();
        if (s.state === 'RESULTS') {
          showResultsScreen(index);
        } else {
          showCountdownScreen(index);
        }
      });
    },
    // Timer tick setup
    (updateFn) => {
      timerUpdateFn = updateFn;
      gameTimer = new CountdownTimer(
        session.mode!.timePerRound,
        (remaining) => {
          engine.updateTime(remaining);
          timerUpdateFn?.(remaining);
        },
        () => {
          // Time's up
          const result = engine.handleTimeout();
          const updatedSession = engine.getSession();
          renderFeedback(result, updatedSession, () => {
            engine.nextRound();
            const s = engine.getSession();
            if (s.state === 'RESULTS') {
              showResultsScreen(index);
            } else {
              showCountdownScreen(index);
            }
          });
        }
      );
      gameTimer.start();
    }
  );
}

function showResultsScreen(index: ScenarioIndex): void {
  gameTimer?.stop();
  const session = engine.getSession();
  track('game_complete', { mode: 'classic', map: session.scenario!.id, score: session.totalPoints });

  renderResults(
    session,
    () => {
      engine.replay();
      trackGameStart('classic', session.scenario!.id);
      showCountdownScreen(index);
    },
    () => showScenarioScreen(index)
  );
}

// ── Flags Screen Controllers ──

function showFlagModeScreen(scenarioId: string, meta: any, index: ScenarioIndex): void {
  flagEngine.goHome();
  showFlagDifficultyScreen(scenarioId, meta, index);
}

function showFlagDifficultyScreen(scenarioId: string, meta: any, index: ScenarioIndex): void {
  renderFlagDifficultySelection(
    meta,
    (difficulty) => {
      showFlagGameModeScreen(scenarioId, meta, difficulty, index);
    },
    () => showScenarioScreen(index)
  );
}

function showFlagGameModeScreen(scenarioId: string, meta: any, difficulty: FlagDifficulty, index: ScenarioIndex): void {
  renderFlagGameModeSelection(
    meta,
    difficulty,
    (mode) => {
      flagEngine.startGame(scenarioId, difficulty, mode).then(() => {
        trackGameStart('flags', scenarioId);
        showFlagCountdownScreen(index);
      });
    },
    () => showFlagDifficultyScreen(scenarioId, meta, index)
  );
}

function showFlagCountdownScreen(index: ScenarioIndex): void {
  const session = flagEngine.getSession();
  renderFlagCountdown(session, () => {
    flagEngine.countdownDone();
    showFlagPlayingScreen(index);
  });
}

function showFlagPlayingScreen(index: ScenarioIndex): void {
  const session = flagEngine.getSession();
  const pool = flagEngine.getAutocompletePool();

  renderFlagPlaying(
    session,
    pool,
    // On choice select
    (code) => {
      gameTimer?.stop();
      const result = flagEngine.handleChoice(code);
      showFlagFeedbackScreen(result, index);
    },
    // On text input submit
    (text) => {
      gameTimer?.stop();
      const result = flagEngine.handleTextAnswer(text);
      showFlagFeedbackScreen(result, index);
    },
    // Timer tick setup
    (updateFn) => {
      timerUpdateFn = updateFn;
      gameTimer = new CountdownTimer(
        15,
        (remaining) => {
          flagEngine.updateTime(remaining);
          timerUpdateFn?.(remaining);
        },
        () => {
          // Timeout
          const result = flagEngine.handleTimeout();
          showFlagFeedbackScreen(result, index);
        }
      );
      gameTimer.start();
    }
  );
}

function showFlagFeedbackScreen(result: any, index: ScenarioIndex): void {
  const updatedSession = flagEngine.getSession();
  renderFlagFeedback(result, updatedSession, () => {
    flagEngine.nextRound();
    const s = flagEngine.getSession();
    if (s.state === 'RESULTS') {
      showFlagResultsScreen(index);
    } else {
      showFlagCountdownScreen(index);
    }
  });
}

function showFlagResultsScreen(index: ScenarioIndex): void {
  gameTimer?.stop();
  const session = flagEngine.getSession();
  track('game_complete', { mode: 'flags', map: session.scenarioId, score: session.totalPoints });

  renderFlagResults(
    session,
    index,
    () => {
      flagEngine.replay().then(() => {
        trackGameStart('flags', session.scenarioId);
        showFlagCountdownScreen(index);
      });
    },
    () => showScenarioScreen(index)
  );
}

// ── Start ──
document.addEventListener('DOMContentLoaded', boot);
